import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to manage SEO metadata dynamically.
 * @param {Object} options - SEO options
 * @param {string} options.title - The page title
 * @param {string} options.description - The page description
 * @param {string} options.canonicalPath - The canonical path (e.g., '/signin')
 * @param {boolean} [options.enabled=true] - Set false when the component is
 *   embedded inside another page. A page that renders another page as a section
 *   ends up with two of these hooks writing to the same <head>, and the child
 *   wins because its effect runs last. That is how the home page came to serve
 *   "Pricing | JobHarvest" as its title and /landing-pages/pricing as its
 *   canonical: Home embeds <PricingPage isDemo /> for the pricing section.
 *   A wrong canonical asks Google to drop the homepage from the index.
 */
const useSEO = ({ title, description, canonicalPath, enabled = true }) => {
  const location = useLocation();
  const domain = 'https://jobharvest.com';

  useEffect(() => {
    if (!enabled) return;

    // Update Title
    if (title) {
      document.title = title;
    }

    // Update Description
    if (description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      }
    }

    // Update Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    const path = (canonicalPath || location.pathname).toLowerCase();

    // Remove all trailing slashes to match the sitemap preference
    const formattedPath = path.replace(/\/$/, '');

    const formattedCanonicalUrl = `${domain}${formattedPath}`;

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', formattedCanonicalUrl);

    // Optional: Update OG URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', formattedCanonicalUrl);
    }

  }, [enabled, title, description, canonicalPath, location.pathname]);
};

export default useSEO;

